import { push } from 'connected-react-router';
import { paths } from '../../../routes/paths';
import CoreAPIService from '../../../utils/core-api';
import { fetchAction } from '../../actions';
import { IssueCategory, IssueStatus, AssigneeArrayByIssueId, TicketingFile, Issue, TicketingUser } from '../api-types';

export const name: string = 'ticketing';

export const defaultProjectKey: string = 'customer';
export const driverProjectKey: string = 'driver';

export const types = {
    GET_ISSUE_STATUS: name + '/GET_ISSUE_STATUS',
    GET_ISSUE_PRIORITIES: name + '/GET_ISSUE_PRIORITIES',
    GET_ISSUE_CATEGORIES: name + '/GET_ISSUE_CATEGORIES',
    GET_ASSIGNEES_FOR_ISSUE: name + '/GET_ASSIGNEES_FOR_ISSUE',
    GET_ISSUE: name + '/GET_ISSUE',
    CREATE_ISSUE: name + '/CREATE_ISSUE',
    UPDATE_ISSUE: name + '/UPDATE_ISSUE',
    CREATE_FILES: name + '/CREATE_FILES',
    DOWNLOAD_FILE: name + '/DOWNLOAD_FILE',
    DELETE_FILE: name + '/DELETE_FILE',
    CLEAR_ISSUE: name + '/CLEAR_ISSUE',
    GET_USER: name + '/GET_USER',
};

interface TicketingState {
    issuesStatus: IssueStatus[];
    issuesPriorities: IssueStatus[];
    issuesCategories: IssueCategory[];
    issueAssignees: AssigneeArrayByIssueId;
    files: TicketingFile[];
    issue: Issue;
    users: TicketingUser[];
}

const initialState: TicketingState = {
    issuesStatus: [],
    issuesPriorities: [],
    issuesCategories: [],
    issueAssignees: {} as AssigneeArrayByIssueId,
    files: [],
    issue: {} as Issue,
    users: [],
};

const reducer = (state = initialState, action): TicketingState => {
    const payload = action.payload;
    const parameters = action.parameters;
    const id = parameters ? parameters.id : 0;

    switch (action.type) {
        case types.GET_ISSUE_STATUS:
            return {
                ...state,
                issuesStatus: payload.issue_status,
            };
        case types.GET_ISSUE_PRIORITIES:
            return {
                ...state,
                issuesPriorities: payload.issue_priorities,
            };
        case types.GET_ISSUE_CATEGORIES:
            return {
                ...state,
                issuesCategories: payload.issue_categories,
            };
        case types.GET_ASSIGNEES_FOR_ISSUE:
            return {
                ...state,
                issueAssignees: {
                    ...state.issueAssignees,
                    [id]: payload.assignees,
                },
            };
        case types.CLEAR_ISSUE:
            return {
                ...state,
                files: [],
                issue: {} as Issue,
            };
        case types.CREATE_FILES:
            return {
                ...state,
                files: [...state.files, ...payload.files],
            };
        case types.DELETE_FILE:
            return {
                ...state,
                files: state.files.filter((f) => f.id !== payload.id),
            };
        case types.GET_ISSUE:
            return {
                ...state,
                issue: payload,
            };
        case types.GET_USER:
            return {
                ...state,
                users: {
                    ...state.users,
                    [id]: payload,
                },
            };
        default:
            return state;
    }
};
export default reducer;

export const actions = {
    getIssueStatus: () => fetchAction(CoreAPIService.get(`/ticketing/issue-status`), types.GET_ISSUE_STATUS, {}),
    getIssuePriorities: () =>
        fetchAction(CoreAPIService.get(`/ticketing/issue-priorities`), types.GET_ISSUE_PRIORITIES, {}),
    getIssueCategories: (projectKey = defaultProjectKey) =>
        fetchAction(
            CoreAPIService.get(`/ticketing/projects/${projectKey}/issue-categories`),
            types.GET_ISSUE_CATEGORIES,
            {},
        ),
    getAssigneesForIssue: (issueId, projectKey = defaultProjectKey) =>
        fetchAction(
            CoreAPIService.get(`/ticketing/projects/${projectKey}/assignees?issue_id=${issueId}`),
            types.GET_ASSIGNEES_FOR_ISSUE,
            { id: issueId },
        ),
    clearIssue: () => ({
        type: types.CLEAR_ISSUE,
        payload: {},
    }),
    createFiles: (files: any) => fetchAction(CoreAPIService.post(`/ticketing/files`, files), types.CREATE_FILES, {}),
    downloadFile: (id: string) => fetchAction(CoreAPIService.get(`/ticketing/files/${id}`), types.DOWNLOAD_FILE, {}),
    deleteFile: (id: string) => ({
        type: types.DELETE_FILE,
        payload: { id },
    }),
    getIssue: (issueId, projectKey = defaultProjectKey) =>
        fetchAction(CoreAPIService.get(`/ticketing/projects/${projectKey}/issues/${issueId}`, null), types.GET_ISSUE, {
            id: issueId,
        }),
    createIssue: (payload, projectKey = defaultProjectKey) =>
        fetchAction(
            CoreAPIService.post(`/ticketing/projects/${projectKey}/issues`, payload),
            types.CREATE_ISSUE,
            {},
            {
                success: (dispatch) => {
                    dispatch(push(`${paths.ticketing}?project=${projectKey}`));
                },
            },
        ),
    updateIssue: (issueId, payload, projectKey = defaultProjectKey) =>
        fetchAction(
            CoreAPIService.put(`/ticketing/projects/${projectKey}/issues/${issueId}`, payload),
            types.UPDATE_ISSUE,
            {},
            {
                success: (dispatch) => {
                    dispatch(push(`${paths.ticketing}?project=${projectKey}`));
                },
            },
        ),
    getUser: (userId) =>
        fetchAction(CoreAPIService.get(`/core/ticketing/users/${userId}`), types.GET_USER, { id: userId }),
};

const localState = (state: any): TicketingState => state[name] || initialState;
export const selectors = {
    users: (state) => localState(state).users,
    issue: (state) => localState(state).issue,
    files: (state) => localState(state).files,
    issuesStatus: (state) => localState(state).issuesStatus,
    issuesPriorities: (state) => localState(state).issuesPriorities,
    issuesCategories: (state) => localState(state).issuesCategories,
    issueAssignees: (state) => localState(state).issueAssignees,
};
